import { CampaignFormData, WorkflowStep } from '../types';
import { callGemini } from './gemini';

interface ReportSection {
  title: string;
  content: string;
  stage: string;
}

interface ConsolidatedReport {
  title: string;
  executiveSummary: string;
  sections: ReportSection[];
  recommendations: string;
  implementationPlan: string;
}

export async function generateConsolidatedReport(
  formData: CampaignFormData,
  steps: WorkflowStep[],
  apiKey: string
): Promise<ConsolidatedReport> {
  try {
    // Generate executive summary prompt
    const executiveSummaryPrompt = `
Generate a comprehensive executive summary for the political campaign analysis of ${formData.core_issue_details.campaign_name} targeting ${formData.core_issue_details.target_entity}.

Campaign Context:
- Political Issue: ${formData.core_issue_details.political_issue}
- Campaign Type: ${formData.core_issue_details.campaign_type}
- Target Entity: ${formData.core_issue_details.target_entity}
- Winning Aspiration: ${formData.campaign_outcome.winning_aspiration}
- Target Audience: ${formData.campaign_outcome.target_audience}

IMPORTANT FORMATTING: Use clean text without any markdown symbols. No **, *, #, ##, ### formatting. Write as professional paragraphs with clear section breaks.

Using the Playing to Win framework analysis, provide:
1. Brief overview of the campaign and political landscape
2. Key strategic insights from the Where to Play analysis
3. Critical elements of the How to Win theory of change
4. Major capability gaps and management system requirements
5. Primary strategic recommendations for campaign success

Format the response as a professional executive summary of 3-4 paragraphs using clean text only.`;

    // Generate strategic recommendations prompt
    const recommendationsPrompt = `
Based on the complete Playing to Win analysis of the ${formData.core_issue_details.campaign_name} political campaign, generate comprehensive strategic recommendations.

Analysis Context:
${steps.map(step => `- ${step.title}: ${step.result?.substring(0, 500)}...`).join('\n')}

IMPORTANT FORMATTING: Use clean text without any markdown symbols. No **, *, #, ##, ### formatting. Use "SECTION X:" format for headers.

Provide strategic recommendations organized by:
SECTION 1: WINNING ASPIRATION REFINEMENT
SECTION 2: WHERE TO PLAY PRIORITIES  
SECTION 3: HOW TO WIN EXECUTION
SECTION 4: CAPABILITY BUILDING
SECTION 5: MANAGEMENT SYSTEM IMPLEMENTATION

Format as clear, actionable recommendations with specific next steps using clean text formatting only.`;

    // Generate implementation plan prompt
    const implementationPrompt = `
Create a detailed implementation roadmap for the ${formData.core_issue_details.campaign_name} political campaign.

Campaign Parameters:
- Winning Aspiration: ${formData.campaign_outcome.winning_aspiration}
- Target Audience: ${formData.campaign_outcome.target_audience}
- Campaign Type: ${formData.core_issue_details.campaign_type}
- Timeline: ${formData.core_issue_details.campaign_timeline}

IMPORTANT FORMATTING: Use clean text without any markdown symbols. No **, *, #, ##, ### formatting. Use "SECTION X:" format for headers.

Based on the Playing to Win analysis, create:
SECTION 1: PHASE 1 (Weeks 1-4) - Foundation and Aspiration Alignment
SECTION 2: PHASE 2 (Weeks 5-12) - Where to Play Implementation
SECTION 3: PHASE 3 (Weeks 13-20) - How to Win Execution
SECTION 4: PHASE 4 (Weeks 21-24) - Capability Building and System Optimization

For each phase include key objectives, specific activities, resource requirements, success metrics, and risk mitigation strategies.

Format as a detailed project timeline with actionable steps using clean text formatting only.`;

    // Execute all prompts in parallel
    const [executiveSummary, recommendations, implementationPlan] = await Promise.all([
      callGemini(executiveSummaryPrompt, apiKey),
      callGemini(recommendationsPrompt, apiKey),
      callGemini(implementationPrompt, apiKey)
    ]);

    // Validate responses
    if (executiveSummary.error || recommendations.error || implementationPlan.error) {
      throw new Error('Failed to generate one or more report sections');
    }

    // Compile report sections with stage grouping
    const sections: ReportSection[] = steps.map(step => ({
      title: step.title,
      content: step.result || 'Analysis pending',
      stage: step.stage
    }));

    return {
      title: `${formData.core_issue_details.campaign_name} - Political Campaign Strategic Plan`,
      executiveSummary: executiveSummary.content,
      sections,
      recommendations: recommendations.content,
      implementationPlan: implementationPlan.content
    };
  } catch (error) {
    console.error('Error generating consolidated report:', error);
    throw error;
  }
}

export function formatConsolidatedReport(report: ConsolidatedReport): string {
  // Group sections by stage
  const stageGroups = report.sections.reduce((acc, section) => {
    if (!acc[section.stage]) {
      acc[section.stage] = [];
    }
    acc[section.stage].push(section);
    return acc;
  }, {} as Record<string, ReportSection[]>);

  const getStageTitle = (stage: string) => {
    switch (stage) {
      case 'aspiration': return 'WINNING ASPIRATION';
      case 'where-to-play': return 'WHERE TO PLAY ANALYSIS';
      case 'how-to-win': return 'HOW TO WIN STRATEGY';
      case 'capabilities': return 'CORE CAPABILITIES ASSESSMENT';
      case 'management': return 'MANAGEMENT SYSTEMS DESIGN';
      default: return 'ANALYSIS';
    }
  };

  return `${report.title}
Playing to Win Framework Analysis
Date: ${new Date().toLocaleDateString()}

EXECUTIVE SUMMARY

${report.executiveSummary}

TABLE OF CONTENTS

1. Executive Summary
2. Playing to Win Strategic Analysis
   - Winning Aspiration
   - Where to Play Analysis
   - How to Win Strategy
   - Core Capabilities Assessment
   - Management Systems Design
3. Strategic Recommendations
4. Implementation Roadmap

PLAYING TO WIN STRATEGIC ANALYSIS

${Object.entries(stageGroups).map(([stage, stageSections]) => `
${getStageTitle(stage)}

${stageSections.map(section => `
${section.title}

${section.content}
`).join('\n')}
`).join('\n')}

STRATEGIC RECOMMENDATIONS

${report.recommendations}

IMPLEMENTATION ROADMAP

${report.implementationPlan}
`;
}
